import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Table, Input, Alert } from 'reactstrap';
import { useAllBoards } from './hooks/useAllBoards';
import { QuickView } from './QuickView';
import { useShortenPcbSn } from './hooks/useShortenPcbSn';

export function GraphDataSelect({ isOpen, toggle, selectedCompanyId = null }) {
    const { allBoards: basicBoards, getAllBoards } = useAllBoards();
    const history = useHistory();
    const { shortenPcbSn } = useShortenPcbSn();

    const [boardsWithData, setBoardsWithData] = useState([]);
    const [selectedBoards, setSelectedBoards] = useState([]);
    const [selectedDataKeys, setSelectedDataKeys] = useState(new Set());
    const [availableDataKeys, setAvailableDataKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setBoardsWithData([]);
            setSelectedBoards([]);
            setSelectedDataKeys(new Set());
            setAvailableDataKeys([]);
            setLoading(true);
            getAllBoards(`api/boards/`);
        } else {
            setBoardsWithData([]);
            setSelectedBoards([]);
            setSelectedDataKeys(new Set());
            setAvailableDataKeys([]);
        }
    }, [isOpen]);

    // Fetch latest data for boards and apply company filter if provided
    useEffect(() => {
        if (!loading || basicBoards.length === 0) return;

        const fetchLatestDataForAll = async () => {
            setDataLoading(true);
            try {
                // Respect company filter from BoardDataPage
                let boardsToProcess = basicBoards;

                if (selectedCompanyId) {
                    boardsToProcess = basicBoards.filter(board =>
                        String(board.companyId) === String(selectedCompanyId)
                    );
                }

                const enriched = await Promise.all(
                    boardsToProcess.map(async (basicBoard) => {
                        try {
                            const resp = await fetch(`api/boardData/pcbSn/${basicBoard.pcbSn}/1`, {
                                method: "GET",
                                headers: {
                                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                                }
                            });

                            if (resp.ok) {
                                const dataArray = await resp.json();
                                if (dataArray.length > 0) {
                                    const latestData = dataArray[0];
                                    return { ...basicBoard, ...latestData };
                                }
                            }
                            return null;
                        } catch (err) {
                            console.warn(`Failed to fetch data for ${basicBoard.pcbSn}`, err);
                            return null;
                        }
                    })
                );

                const validBoards = enriched.filter(board => board !== null);
                setBoardsWithData(validBoards);

                // Collect all numeric data keys from dataList (dataRev 1.1)
                const numericKeys = new Set();
                validBoards.forEach(board => {
                    if (board.dataRev === "1.1" && board.dataList) {
                        Object.entries(board.dataList).forEach(([key, value]) => {
                            if (value !== null && value !== undefined && value !== "") {
                                const num = Number(value);
                                if (!isNaN(num) && isFinite(num)) {
                                    numericKeys.add(key);
                                }
                            }
                        });
                    }
                });

                setAvailableDataKeys(Array.from(numericKeys).sort());

            } catch (err) {
                console.error("Error enriching boards with latest data", err);
            } finally {
                setLoading(false);
                setDataLoading(false);
            }
        };

        fetchLatestDataForAll();
    }, [basicBoards, loading, selectedCompanyId]);

    const handleBoardSelect = (board, e) => {
        if (e.target.checked) {
            setSelectedBoards(prev => [...prev, board]);
        } else {
            setSelectedBoards(prev => prev.filter(b => b.id !== board.id));
        }
    };

    const handleSelectAllBoards = (e) => {
        if (e.target.checked) {
            setSelectedBoards([...boardsWithData]);
        } else {
            setSelectedBoards([]);
        }
    };

    const toggleDataKey = (key) => {
        setSelectedDataKeys(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) newSet.delete(key);
            else newSet.add(key);
            return newSet;
        });
    };

    const handleGraph = () => {
        if (selectedBoards.length === 0 || selectedDataKeys.size === 0) {
            alert("Please select at least one board and one data field.");
            return;
        }

        history.push('/graph', {
            selectedBoards,
            selectedDataKeys: Array.from(selectedDataKeys)
        });
    };

    // Build 8-column rows for data fields
    const buildDataFieldRows = () => {
        const rows = [];
        let currentRow = [];
        let hasSeenG1 = false;

        availableDataKeys.forEach(key => {
            const isG1 = key.startsWith("g1_");

            if (isG1 && !hasSeenG1 && currentRow.length > 0) {
                rows.push([...currentRow]);
                currentRow = [];
                hasSeenG1 = true;
            }

            currentRow.push(key);

            if (currentRow.length === 8) {
                rows.push([...currentRow]);
                currentRow = [];
            }
        });

        if (currentRow.length > 0) {
            rows.push(currentRow);
        }

        return rows;
    };

    const dataFieldRows = buildDataFieldRows();

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" backdrop="static">
            <ModalHeader toggle={toggle}>
                Graph Data — Select Boards &amp; Fields (dataRev 1.1)
                {selectedCompanyId && <span className="text-muted ms-3 small">(Company Filtered)</span>}
            </ModalHeader>
            <ModalBody>
                {loading || dataLoading ? (
                    <Alert color="info">
                        {loading ? "Loading board list..." : "Fetching latest data for boards... (this may take a moment)"}
                    </Alert>
                ) : (
                    <>
                        <Alert color="info">
                            Only boards with available numeric data (dataRev 1.1) are shown.
                            {selectedCompanyId && " Results are limited to the currently selected company."}
                        </Alert>

                        {/* Boards Selection Table */}
                        <div className="mb-4">
                            <h5>
                                Select Boards ({boardsWithData.length} total with data)
                                {selectedCompanyId && " — Company Filtered"}
                            </h5>
                            <Table striped responsive size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '80px', textAlign: 'center' }}>
                                                <Input
                                                    className='centered'
                                                    type="checkbox"
                                                    onChange={handleSelectAllBoards}
                                                    checked={selectedBoards.length === boardsWithData.length && boardsWithData.length > 0}
                                                />
                                        </th>
                                        <th>Pcb SN</th>
                                        <th>Pcb MN</th>
                                        <th>Group</th>
                                        <th>Hardware Timestamp</th>
                                        <th style={{ width: '120px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {boardsWithData.map(board => (
                                        <tr key={board.id}>
                                            <td>
                                                <Input
                                                        className="centered"
                                                        type="checkbox"
                                                        checked={selectedBoards.some(b => b.id === board.id)}
                                                        onChange={(e) => handleBoardSelect(board, e)}
                                                    />
                                            </td>
                                            <td>
                                                <span className="code">{shortenPcbSn(board.pcbSn)}</span>
                                            </td>
                                            <td>{board.pcbMn}</td>
                                            <td>{board.group || '-'}</td>
                                            <td>{board.timeSent || '—'}</td>
                                            <td>
                                                <QuickView board={board} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {/* Numeric Data Fields - 8 Column Clickable Table */}
                        <div className="mb-4">
                            <h5>Select Numeric Data Fields to Graph (click to select)</h5>
                            {availableDataKeys.length === 0 ? (
                                <Alert color="warning">
                                    No numeric fields found in any board&apos;s <code>dataList</code>.
                                </Alert>
                            ) : (
                                <Table bordered size="sm" className="data-fields-table">
                                    <tbody>
                                        {dataFieldRows.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {row.map((key) => (
                                                    <td
                                                        key={key}
                                                        style={{ width: '12.5%', padding: '10px 8px', cursor: 'pointer' }}
                                                        className={selectedDataKeys.has(key) ? 'selected-field' : ''}
                                                        onClick={() => toggleDataKey(key)}
                                                    >
                                                        <span className="field-name">{key}</span>
                                                    </td>
                                                ))}
                                                {Array(Math.max(0, 8 - row.length)).fill(null).map((_, i) => (
                                                    <td key={`empty-${rowIndex}-${i}`} style={{ width: '12.5%' }}></td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={handleGraph}
                    disabled={selectedBoards.length === 0 || selectedDataKeys.size === 0 || loading || dataLoading}
                >
                    Continue to Graph ({selectedBoards.length} boards • {selectedDataKeys.size} fields)
                </Button>
            </ModalFooter>
        </Modal>
    );
}